import React, { useState } from "react";
import { HeartPulse, Loader2, CheckCircle2, Clock3 } from "lucide-react";
import CalculatorShell from "@/components/tools/CalculatorShell";
import { NumberInput, Toggle } from "@/components/tools/Inputs";
import ResultsCard from "@/components/tools/ResultsCard";
import MedicareWaitlist from "@/components/MedicareWaitlist";
import { postTool } from "@/lib/api";
import { buildApplyQuery } from "@/lib/format";

export default function MedicareEligibility() {
  const [form, setForm] = useState({
    current_age: 60,
    is_citizen_or_pr_5yrs: true,
    on_ssdi: false,
    has_esrd_or_als: false,
  });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const validate = () => {
    const e = {};
    if (form.current_age < 0 || form.current_age > 110)
      e.current_age = "Age must be 0–110.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    setServerError("");
    setResult(null);
    try {
      await new Promise((r) => setTimeout(r, 500));
      const data = await postTool("medicare-eligibility", form);
      setResult(data);
    } catch {
      setServerError("Could not check eligibility. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const headline = result
    ? result.eligible_now
      ? "You're eligible for Medicare now."
      : `You'll be eligible in ${result.months_until_eligible} months.`
    : "";

  const subhead = result
    ? result.eligibility_date
      ? `Estimated eligibility date: ${new Date(result.eligibility_date).toLocaleDateString(
          "en-US",
          { year: "numeric", month: "long", day: "numeric" },
        )}.`
      : null
    : null;

  return (
    <CalculatorShell
      title="Medicare Eligibility Checker"
      blurb="Confirm if you're eligible now — or how many months until you are."
      duration="Takes ~30 seconds"
      Icon={HeartPulse}
    >
      <form
        onSubmit={onSubmit}
        data-testid="medicare-form"
        className="bg-white rounded-2xl border border-lw-navy/10 p-6 sm:p-8 space-y-6"
      >
        <NumberInput
          label="Your current age"
          value={form.current_age}
          onChange={set("current_age")}
          min={0}
          max={110}
          testId="med-age"
          error={errors.current_age}
        />

        <Toggle
          label="US citizen or 5+ year permanent resident"
          value={form.is_citizen_or_pr_5yrs}
          onChange={set("is_citizen_or_pr_5yrs")}
          testId="med-citizen"
        />
        <Toggle
          label="Currently receiving Social Security Disability (SSDI)"
          helper="Eligible after 24 months of SSDI."
          value={form.on_ssdi}
          onChange={set("on_ssdi")}
          testId="med-ssdi"
        />
        <Toggle
          label="Have ESRD or ALS"
          helper="Either qualifies you immediately, regardless of age."
          value={form.has_esrd_or_als}
          onChange={set("has_esrd_or_als")}
          testId="med-esrd"
        />

        {serverError && (
          <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
            {serverError}
          </p>
        )}

        <button
          type="submit"
          disabled={loading}
          data-testid="medicare-submit"
          className="btn-gold w-full sm:w-auto disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="animate-spin" size={18} /> Checking…
            </>
          ) : (
            <>Check my eligibility →</>
          )}
        </button>
      </form>

      {result && (
        <ResultsCard
          source="medicare-eligibility"
          headline={headline}
          subhead={subhead}
          rows={[
            {
              label: "Eligibility pathway",
              value: result.eligible_now ? (
                <span className="inline-flex items-center gap-1.5">
                  <CheckCircle2 size={16} className="text-lw-gold" /> Active
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5">
                  <Clock3 size={16} className="text-lw-gold" /> Pending
                </span>
              ),
              testId: "pathway-status",
            },
          ]}
          assumptions={[result.pathway, ...(result.assumptions || [])]}
          matchedIds={[]}
          products={[]}
          applyQuery={buildApplyQuery({
            source: "medicare",
            age: form.current_age,
            eligible: result.eligible_now ? "now" : "future",
          })}
          toolSlug="medicare-eligibility"
          toolInputs={form}
          toolResults={result}
        />
      )}

      {result && result.supplement_vs_advantage && (
        <div
          data-testid="medicare-comparison"
          className="rounded-2xl border border-lw-navy/10 bg-lw-ivory-warm p-6 sm:p-8"
        >
          <span className="lw-eyebrow">Supplement vs Advantage</span>
          <p className="mt-3 text-sm text-lw-navy/80 leading-relaxed">
            {result.supplement_vs_advantage}
          </p>
          <p className="mt-4 pt-4 border-t border-lw-navy/10 text-[11px] italic text-lw-muted leading-relaxed">
            Educational eligibility estimate only. When our Medicare offerings
            launch, we&apos;ll be in touch. Meanwhile, visit Medicare.gov for
            plan-specific information.
          </p>
        </div>
      )}

      {result && (
        <MedicareWaitlist
          context={{ age: form.current_age, eligible: result.eligible_now ? "now" : "future" }}
        />
      )}
    </CalculatorShell>
  );
}
